import React, { useCallback, useContext, useMemo } from 'react';
import { useTypedQuery } from 'src/graphql/hooks/useTypedApollo';
import {
    GetMeQueryNode,
    RealSessionDetailsFragment,
    SessionDetailsFragment,
} from 'src/graphql/lib';
import { GetMeDocument } from 'src/graphql/lib/typed-documents';
import { getIntegerId } from 'src/helpers/getIntegerId';
import { AnalyticsContextProvider } from 'src/lib/analytics/context';
import { AppLoadingSurface } from './components/AppLoadingSurface';

interface Value {
    me: SessionDetailsFragment | null;
    realMe: RealSessionDetailsFragment | null;
    refetchSession: () => Promise<unknown>;
}

const context = React.createContext<Value | null>(null);

const BASIC_TRADES_SUBSCRIPTION_ENTITLEMENTS = new Set(['website', 'vendor.website']);
const GROWTH_TRADES_SUBSCRIPTION_ENTITLEMENTS = new Set([
    'website.premium',
    'vendor.website.premium',
    'nora',
    'ads',
]);

const getTradesSubscriptionTier = (
    entitlements: readonly string[] | null | undefined,
): 'basic' | 'growth' | 'none' => {
    if (
        entitlements?.some((entitlement) =>
            GROWTH_TRADES_SUBSCRIPTION_ENTITLEMENTS.has(entitlement),
        )
    ) {
        return 'growth';
    }

    if (
        entitlements?.some((entitlement) => BASIC_TRADES_SUBSCRIPTION_ENTITLEMENTS.has(entitlement))
    ) {
        return 'basic';
    }

    return 'none';
};

const isSessionDetailsFragment = (
    node: GetMeQueryNode['me'] | null | undefined,
): node is SessionDetailsFragment => {
    if (!node || node.__typename !== 'UserNode') {
        return false;
    }
    return 'isActive' in node;
};

const isRealSessionDetailsFragment = (
    node: GetMeQueryNode['realMe'] | null | undefined,
): node is RealSessionDetailsFragment => {
    if (!node || node.__typename !== 'UserNode') {
        return false;
    }
    return !('isActive' in node);
};

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
    const sessionResult = useTypedQuery(GetMeDocument);
    const rawMe = (sessionResult.data?.me ?? null) as GetMeQueryNode['me'] | null;
    const rawRealMe = (sessionResult.data?.realMe ?? null) as GetMeQueryNode['realMe'] | null;

    const me = isSessionDetailsFragment(rawMe) ? rawMe : null;
    const realMe = isRealSessionDetailsFragment(rawRealMe) ? rawRealMe : null;
    const refetchSession = sessionResult.refetch;
    const stableRefetchSession = useCallback(() => refetchSession(), [refetchSession]);

    const userId = me?.id ? getIntegerId(me.id) : null;
    const attributedUserId = realMe?.id ? getIntegerId(realMe.id) : null;
    const tradesSubscriptionTier = getTradesSubscriptionTier(me?.entitlements);

    // useMemo required - context values passed to providers.
    // React Compiler doesn't optimize context values automatically.
    const sessionValue = useMemo(
        () => ({ me, realMe, refetchSession: stableRefetchSession }),
        [me, realMe, stableRefetchSession],
    );
    const analyticsValue = useMemo(
        () => ({
            userId: userId,
            attributedUserId: attributedUserId,
            tradesSubscriptionTier,
            hasGrowthTier: tradesSubscriptionTier === 'growth',
        }),
        [userId, attributedUserId, tradesSubscriptionTier],
    );

    const isInitialLoading = sessionResult.loading && !sessionResult.data;

    if (isInitialLoading) {
        return <AppLoadingSurface />;
    }

    return (
        <context.Provider value={sessionValue}>
            <AnalyticsContextProvider value={analyticsValue}>{children}</AnalyticsContextProvider>
        </context.Provider>
    );
};

export function useSession() {
    const value = useContext(context);
    if (!value) {
        throw new Error('SessionProvider is not detected.');
    }
    return value;
}

export function useNullableSessionUser() {
    const { me } = useSession();
    return me;
}

export function useSessionUser() {
    const { me } = useSession();
    if (!me) {
        throw new Error('Session is not ready.');
    }
    return me;
}

export function useRealSessionUser() {
    const { realMe } = useSession();
    return realMe;
}
